<template>
  <div>
    <v-btn :to="{ name: 'OfficeUser' }" color="primary" plain>
      <v-icon left>fa-arrow-left</v-icon>
      ย้อนกลับ
    </v-btn>

    <v-container style="max-width: 600px">
      <v-card>
        <v-card-title>แก้ไขผู้ใช้งาน</v-card-title>
        <validation-observer ref="observer">
          <v-form ref="form" @submit.prevent="submitUpdateUser">
            <v-card-text>
              <validation-provider
                v-slot="{ errors }"
                name="username"
                :rules="rules.username"
              >
                <v-text-field
                  v-model="formData.username"
                  :error-messages="errors"
                  label="username*"
                  outlined
                  dense
                  required
                ></v-text-field>
              </validation-provider>
              <v-row class="mt-1">
                <v-col class="py-0" cols="12" md="6">
                  <validation-provider
                    v-slot="{ errors }"
                    name="first_name"
                    :rules="rules.first_name"
                  >
                    <v-text-field
                      v-model="formData.first_name"
                      :error-messages="errors"
                      label="ชื่อ*"
                      outlined
                      dense
                      required
                    ></v-text-field>
                  </validation-provider>
                </v-col>
                <v-col class="py-0" cols="12" md="6">
                  <validation-provider
                    v-slot="{ errors }"
                    name="last_name"
                    :rules="rules.last_name"
                  >
                    <v-text-field
                      v-model="formData.last_name"
                      :error-messages="errors"
                      label="สกุล*"
                      outlined
                      dense
                      required
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <div class="text-h6">สถานะ*</div>
              <v-chip-group
                v-model="selectRoles"
                column
                multiple
                @change="onUserRoleChange"
              >
                <v-chip
                  v-for="(role, index) in roles"
                  :key="index"
                  filter
                  outlined
                >
                  {{ role.name }}
                </v-chip>
              </v-chip-group>
              <v-row class="mt-4">
                <v-col
                  v-if="under_faculty"
                  class="py-0"
                  cols="12"
                  :md="under_major ? 6 : 12"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    vid="faculty_id"
                    name="faculty_id"
                    :rules="rules.faculty_id"
                  >
                    <v-select
                      v-model="formData.faculty_id"
                      :error-messages="errors"
                      label="คณะ"
                      :items="faculties"
                      item-value="id"
                      item-text="name"
                      outlined
                      dense
                      @change="onFacultyChange"
                    >
                    </v-select>
                  </validation-provider>
                </v-col>
                <v-col v-if="under_major" class="py-0" cols="12" md="6">
                  <validation-provider
                    v-slot="{ errors }"
                    vid="major_id"
                    name="major_id"
                    :rules="rules.major_id"
                  >
                    <v-select
                      v-model="formData.major_id"
                      :error-messages="errors"
                      label="สาขา"
                      :items="option_majors"
                      item-value="id"
                      item-text="name"
                      outlined
                      dense
                      :disabled="!(under_faculty && formData.faculty_id)"
                    >
                    </v-select>
                  </validation-provider>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" type="submit" :loading="submitLoading">
                บันทึก
              </v-btn>
            </v-card-actions>
          </v-form>
        </validation-observer>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import _ from "lodash"
import * as UserService from "@/common/service/user.service"
import * as RoleService from "@/common/service/role.service"
import { formValidateError } from "@/common/helper"

import {
  UPDATE_BREADCRUMBS,
  GET_YEARS,
  GET_FACULTIES,
  GET_MAJORS,
  UPDATE_USER,
} from "@/store/action.type"

export default {
  name: "OfficeUserEdit",
  title: "แก้ไขผู้ใช้งาน",
  created() {
    this.$store.dispatch(UPDATE_BREADCRUMBS, [
      {
        text: "ผู้ใช้งาน",
        disabled: false,
        exact: true,
        to: { name: "OfficeUser" },
      },
      {
        text: "แก้ไขผู้ใช้งาน",
        disabled: true,
        exact: true,
        to: { name: "OfficeUserEdit" },
      },
    ])
  },
  data() {
    return {
      user: {},
      formData: {
        username: "",
        first_name: "",
        last_name: "",
        year_id: "",
        faculty_id: "",
        major_id: "",
        roles: [],
      },
      rules: {
        username: "required",
        first_name: "required",
        last_name: "required",
        under_year: "required_if:under_faculty,false",
        under_faculty: "required_if:under_year,false",
        year_id: "required_if:under_year",
        faculty_id: "required_if:under_faculty",
        major_id: "required_if:under_faculty",
      },
      under_faculty: false,
      under_major: false,
      option_majors: [],
      submitLoading: false,

      roles: [],
      selectRoles: [],
    }
  },
  computed: {
    ...mapGetters(["years", "faculties", "majors", "userError"]),
  },
  async mounted() {
    this.$store.dispatch(GET_YEARS)
    this.$store.dispatch(GET_FACULTIES)
    this.$store.dispatch(GET_MAJORS)

    await this.getUser()
    this.initUser()
    await this.getRole()
    this.initUserRole()
  },
  methods: {
    async getUser() {
      return new Promise(async (resolve) => {
        const userId = this.$route.params.user
        const res = await UserService.show(userId)
        this.user = res.user

        this.formData.username = res.user.username
        this.formData.first_name = res.user.first_name
        this.formData.last_name = res.user.last_name
        resolve()
      })
    },
    async getRole() {
      return new Promise(async (resolve) => {
        const res = await RoleService.index()
        this.roles = res.roles
        resolve()
      })
    },
    async initUser() {
      if (this.user.faculty.id) {
        this.under_faculty = true
        this.formData.faculty_id = this.user.faculty.id
      }

      if (this.user.major.id) {
        this.under_major = true
        this.formData.major_id = this.user.major.id
        this.onFacultyChange(this.user.faculty.id)
      }
    },
    initUserRole() {
      const userRoles = this.user.roles.map((userRole) => userRole.id)
      this.selectRoles = this.roles
        .map((role, index) => {
          if (userRoles.includes(role.id)) {
            return index
          }
        })
        .filter((role_id) => role_id !== undefined)
    },
    onUnderYearChange(value) {
      if (!value) {
        this.formData.year_id = ""
      }
    },
    onUnderFacultyChange(value) {
      if (!value) {
        this.formData.faculty_id = ""
        this.formData.major_id = ""
      }
    },
    onFacultyChange(faculty_id) {
      this.option_majors = this.majors.filter(
        (major) => major.faculty_id === faculty_id
      )
    },
    async submitUpdateUser() {
      this.submitLoading = true
      const validated = await this.$refs.observer.validate()

      if (validated) {
        this.formData.roles = this.selectRoles.map(roleIndex => this.roles[roleIndex].id )
        
        await this.$store.dispatch(UPDATE_USER, {
          user_id: this.user.id,
          data: this.formData,
        })

        if (!_.isEmpty(this.userError)) {
          formValidateError(this, this.userError)
        }
      }

      this.submitLoading = false
    },
    onUserRoleChange(roles) {
      const hasFacultyManager = roles.includes(2)
      const hasCourseManager = roles.includes(3)

      this.under_faculty = hasFacultyManager || hasCourseManager
      this.under_major = hasCourseManager

      this.rules.faculty_id =
        hasFacultyManager || hasCourseManager ? "required" : ""
      this.rules.major_id = hasCourseManager ? "required" : ""

      if (!hasFacultyManager) {
        this.formData.faculty_id = ""
      }

      if (!hasCourseManager) {
        this.formData.major_id = ""
      }
    },
  },
}
</script>
